import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, ArrowRight, AlertOctagon } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

type UpgradePlan = 'studio' | 'team';

const PLAN_LABEL: Record<UpgradePlan, string> = { studio: 'Studio', team: 'Team' };
const PLAN_PRICE: Record<UpgradePlan, string> = { studio: '€49/mese', team: '€149/mese' };

interface UpsellTooltipProps {
  /** Piano minimo richiesto per sbloccare la feature. */
  plan: UpgradePlan;
  /** Nome leggibile della feature (es. "Vista 3D Fotorealistica"). */
  feature: string;
  /** Lato del tooltip rispetto al child. Default: 'top'. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: React.ReactElement;
}

/**
 * Tooltip standard per i gate dei piani.
 * Avvolge un elemento (tipicamente un trigger disabled, un menu item o un tab)
 * e mostra al hover "Disponibile da [plan] · Passa a [plan] →".
 * Il bottone naviga a /settings/billing.
 */
export function UpsellTooltip({ plan, feature, side = 'top', children }: UpsellTooltipProps) {
  const navigate = useNavigate();
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-[280px] p-3 bg-white shadow-lg border">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Crown className="h-3.5 w-3.5 text-amber-500" />
            <span className="font-semibold text-xs">Disponibile da {PLAN_LABEL[plan]}</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            <span className="font-medium text-foreground">{feature}</span> richiede il piano {PLAN_LABEL[plan]} ({PLAN_PRICE[plan]}).
          </p>
          <Button
            size="sm"
            className="w-full h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/settings/billing');
            }}
          >
            Passa a {PLAN_LABEL[plan]}
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface WriteBlockedTooltipProps {
  /** Etichetta breve dell'azione bloccata, es: "Salva stratigrafia". */
  action: string;
  /** Side del tooltip. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: React.ReactElement;
}

/**
 * Variante del tooltip per quando la subscription è cancelled/past_due.
 * Colore rosso (allarme) + CTA "Riattiva abbonamento" → /settings/billing.
 *
 * Usa lo stesso pattern di UpsellTooltip ma comunica un'altra dimensione:
 * non "feature mancante" ma "subscription scaduta · app in sola lettura".
 */
export function WriteBlockedTooltip({ action, side = 'top', children }: WriteBlockedTooltipProps) {
  const navigate = useNavigate();
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-[280px] p-3 bg-white shadow-lg border border-red-200">
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertOctagon className="h-3.5 w-3.5 text-red-600" />
            <span className="font-semibold text-xs text-red-700">Abbonamento scaduto</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            <span className="font-medium text-foreground">{action}</span> richiede una subscription attiva. I tuoi dati sono ancora visibili ma le scritture sono disabilitate.
          </p>
          <Button
            size="sm"
            variant="destructive"
            className="w-full h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/settings/billing');
            }}
          >
            Riattiva abbonamento
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface UpsellBannerProps {
  plan: UpgradePlan;
  feature: string;
  description?: string;
  className?: string;
}

/**
 * Banner inline a tutta larghezza per i casi in cui sostituiamo un blocco
 * di feature (es. drawing tools mappa) con il messaggio upsell.
 */
export function UpsellBanner({ plan, feature, description, className }: UpsellBannerProps) {
  const navigate = useNavigate();
  return (
    <div className={`rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50/50 p-4 ${className ?? ''}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2 flex-shrink-0">
          <Crown className="h-4 w-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-0.5">
            {feature} · disponibile da {PLAN_LABEL[plan]}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mb-2">{description}</p>
          )}
          <Button
            size="sm"
            variant="default"
            className="h-7 text-xs"
            onClick={() => navigate('/settings/billing')}
          >
            Passa a {PLAN_LABEL[plan]} ({PLAN_PRICE[plan]})
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface WriteBlockedBannerProps {
  /** Etichetta breve dell'azione/feature bloccata. Es: "Vista 3D Fotorealistica". */
  feature: string;
  description?: string;
  className?: string;
}

/**
 * Variante banner rossa per quando la subscription è cancelled/past_due.
 * Sostituisce visivamente UpsellBanner in tutti i punti in cui la priorità
 * è canWrite > feature flag (es. quando l'utente ha l'abbonamento scaduto
 * e quindi l'upsell di piano superiore non ha senso: deve PRIMA riattivare).
 */
export function WriteBlockedBanner({ feature, description, className }: WriteBlockedBannerProps) {
  const navigate = useNavigate();
  return (
    <div className={`rounded-lg border border-red-200 bg-gradient-to-br from-red-50 to-rose-50/50 p-4 ${className ?? ''}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-red-100 p-2 flex-shrink-0">
          <AlertOctagon className="h-4 w-4 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-0.5 text-red-700">
            Abbonamento scaduto · {feature} non disponibile
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            {description ?? 'I tuoi dati sono ancora visibili in sola lettura. Riattiva l\'abbonamento per usare di nuovo questa funzionalità.'}
          </p>
          <Button
            size="sm"
            variant="destructive"
            className="h-7 text-xs"
            onClick={() => navigate('/settings/billing')}
          >
            Riattiva abbonamento
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
